import { useLocation, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Terminal, ShieldAlert, Home, HelpCircle, ArrowLeft } from "lucide-react";

const NotFound = () => {
  const location = useLocation();
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  const faqs = [
    {
      q: "Why am I seeing this 404 error page?",
      a: "This happens when the requested page URL is mistyped, moved, or deleted. Our system returns a 404 status code indicating the route could not be resolved."
    },
    {
      q: "How can I access Evil Markhors Services?",
      a: "Simply use the navigation pathways below or return to our homepage. All our major portals (SMM Panel, AI Tools Shop, Academy) are active and functional."
    }
  ];

  return (
    <>
      <Helmet>
        <title>404 Page Not Found | Evil Markhors Services</title>
        <meta name="description" content="Requested page not found. Return to Evil Markhors Services to access Pakistan's cheapest SMM panel, AI tool store, and cybersecurity VAPT." />
        <meta name="robots" content="noindex, follow" />
        <meta name="author" content="Evil Markhors Security Team" />
        <meta name="date" content="2026-06-23" />
        <meta name="last-modified" content="2026-07-29" />

        {/* Structured Data Graph for SEO/AEO/GEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": "https://evilmarkhors.tech/#organization",
                "name": "Evil Markhors Services",
                "url": "https://evilmarkhors.tech",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://evilmarkhors.tech/images/evil-markhors-logo.webp",
                  "width": "512",
                  "height": "512"
                }
              },
              {
                "@type": "WebPage",
                "@id": "https://evilmarkhors.tech/404/#page",
                "url": "https://evilmarkhors.tech/404",
                "name": "404 Page Not Found | Evil Markhors Services",
                "description": "The requested page was not found. Navigate back to Evil Markhors Services to access Pakistan's cheapest SMM panels, web hosting, and cybersecurity VAPT.",
                "publisher": { "@id": "https://evilmarkhors.tech/#organization" },
                "datePublished": "2026-06-23T00:00:00+05:00",
                "dateModified": "2026-07-03T00:00:00+05:00"
              },
              {
                "@type": "TechArticle",
                "@id": "https://evilmarkhors.tech/404/#article",
                "headline": "Understanding HTTP 404 Errors and Resolution Steps",
                "description": "An educational guide on why HTTP 404 errors occur and how to navigate back to active services.",
                "inLanguage": "en-US",
                "about": {
                  "@type": "Thing",
                  "name": "HTTP 404",
                  "sameAs": "https://www.wikidata.org/wiki/Q11014"
                },
                "mainEntityOfPage": "https://evilmarkhors.tech/404",
                "datePublished": "2026-06-23T00:00:00+05:00",
                "dateModified": "2026-07-03T00:00:00+05:00",
                "author": {
                  "@type": "Organization",
                  "name": "Evil Markhors Security Team"
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background text-foreground relative overflow-hidden flex items-center justify-center p-4">
        {/* Decorative background grids */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />

        <div className="max-w-2xl w-full mx-auto relative z-10 my-8">
          
          {/* Main Error Terminal Card */}
          <div className="rounded-lg border border-red-500/30 bg-card/40 backdrop-blur-md p-6 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-red-500" />
            
            {/* Header Info */}
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3 mb-6">
              <div className="flex items-center gap-2 text-red-500 text-xs font-mono">
                <ShieldAlert className="w-4 h-4 animate-pulse" />
                <span>SECURE_ALERT: RESOLUTION_FAILURE</span>
              </div>
              <span className="font-mono text-[10px] text-muted-foreground uppercase">
                Last Sync: <time dateTime="2026-07-03">2026-07-03</time>
              </span>
            </div>

            {/* Main Heading */}
            <header className="mb-6 text-center sm:text-left">
              <h1 className="text-3xl sm:text-4xl font-bold font-mono text-red-500 text-glow mb-2">
                404 - PATH NOT FOUND
              </h1>
              <p className="font-mono text-xs text-muted-foreground">
                Requested Resource: <code className="text-red-400 bg-red-950/20 px-1.5 py-0.5 rounded">{location.pathname}</code>
              </p>
            </header>

            {/* Direct Answer/Definition (GEO Friendly) */}
            <section className="mb-6 border-l-2 border-red-500/30 pl-4 py-1">
              <h2 className="text-xs font-bold font-mono text-foreground uppercase tracking-wider mb-2">
                What is a 404 Error?
              </h2>
              <p className="font-mono text-xs text-muted-foreground leading-relaxed">
                An HTTP 404 error is defined as a standard server-side response indicating that the requested page is missing. Statistically, over 99.9% of our application routes are active. A 404 response indicating a broken URL represents less than 0.1% of our network pathways.
              </p>
            </section>

            {/* Common Status Codes Comparison Table */}
            <section className="mb-6">
              <h2 className="text-xs font-bold font-mono text-foreground uppercase tracking-wider mb-3">
                Error Status Reference
              </h2>
              <div className="overflow-x-auto font-mono">
                <table className="w-full text-left border-collapse text-[10px]">
                  <thead>
                    <tr className="border-b border-border/80 text-muted-foreground">
                      <th className="py-2 px-3">Status Code</th>
                      <th className="py-2 px-3">Standard Meaning</th>
                      <th className="py-2 px-3">Resolution Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40 text-muted-foreground">
                    <tr>
                      <td className="py-2 px-3 text-red-400 font-bold">404 Not Found</td>
                      <td className="py-2 px-3">Resource does not exist</td>
                      <td className="py-2 px-3 text-foreground">Verify URL path or return home</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3 text-yellow-400 font-bold">403 Forbidden</td>
                      <td className="py-2 px-3">Access permission denied</td>
                      <td className="py-2 px-3">Authentication required</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3 text-blue-400 font-bold">500 Server Error</td>
                      <td className="py-2 px-3">Internal transaction failed</td>
                      <td className="py-2 px-3">Retry action after 5 minutes</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Navigation Lists */}
            <section className="mb-6">
              <h2 className="text-xs font-bold font-mono text-foreground uppercase tracking-wider mb-3">
                Recommended Action Pathways
              </h2>
              <ul className="list-none space-y-2 font-mono text-xs text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Terminal className="w-3.5 h-3.5 text-primary shrink-0" />
                  <span>Explore the primary <Link to="/" className="text-primary hover:underline font-bold">Homepage</Link> index.</span>
                </li>
                <li className="flex items-center gap-2">
                  <Terminal className="w-3.5 h-3.5 text-primary shrink-0" />
                  <span>Check our premium <Link to="/ai-tools" className="text-primary hover:underline font-bold">AI Tools Shop</Link> catalogue.</span>
                </li>
                <li className="flex items-center gap-2">
                  <Terminal className="w-3.5 h-3.5 text-primary shrink-0" />
                  <span>Apply for cybersecurity labs in the <Link to="/academy" className="text-primary hover:underline font-bold">Evil Markhors Academy</Link>.</span>
                </li>
              </ul>
            </section>

            {/* Simple FAQ Accordion */}
            <section className="border-t border-border/60 pt-4">
              <h2 className="text-xs font-bold font-mono text-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <HelpCircle className="w-4 h-4 text-primary" /> Resolution FAQs
              </h2>
              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <div key={index} className="border-b border-border/20 pb-2">
                    <button
                      className="w-full flex justify-between items-center text-left font-mono text-xs text-foreground hover:text-primary transition-colors focus:outline-none"
                      onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                    >
                      <span>{faq.q}</span>
                      <span className="text-primary font-bold ml-2">
                        {activeFaq === index ? "[-]" : "[+]"}
                      </span>
                    </button>
                    {activeFaq === index && (
                      <p className="mt-2 font-mono text-[11px] text-muted-foreground leading-relaxed pl-2 border-l border-primary/40">
                        {faq.a}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Back Button */}
            <div className="mt-6 pt-4 border-t border-border/60 flex items-center justify-between">
              <Link to="/" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-mono text-xs transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to Safety
              </Link>
              <span className="text-[9px] font-mono text-muted-foreground tracking-widest uppercase">
                Author: Evil Markhors Team
              </span>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default NotFound;
